/**
 * Reusable order badge components — status and payment badges
 * for use across the checkout dialog, orders page, and consultation form.
 */
import { Banknote, MessageSquareText, Package, PackageCheck, PackageX } from 'lucide-react'

import type { OrderStatus, PaymentStatus } from '@snootsvet/shared'

import { Badge } from '@/frontend/components/ui/badge'
import { getPaymentDisplay } from '@/frontend/features/orders/orders.utils'

/**
 * Human-readable label for each order status.
 * TODO: `returned` is not reachable yet — reversal logic is deferred. See PR #43 discussion.
 */
export const statusLabel: Record<OrderStatus, string> = {
  pending: 'Pending',
  prepared: 'Ready',
  collected: 'Collected',
  administered: 'Administered',
  out_of_stock: 'Out of Stock',
  returned: 'Returned',
  cancelled: 'Cancelled',
}

/** Badge variant for each order status. */
export const ORDER_STATUS_VARIANT = {
  pending: 'pending',
  prepared: 'info',
  collected: 'neutral',
  administered: 'neutral',
  out_of_stock: 'warning',
  returned: 'destructive',
  cancelled: 'destructive',
} as const

// ---------------------------------------------------------------------------
// OrderStatusBadge — coloured pill showing order fulfillment status
// ---------------------------------------------------------------------------

interface OrderStatusBadgeProps {
  status: OrderStatus
  size?: React.ComponentPropsWithoutRef<typeof Badge>['size']
  title?: string
}

/** Icon per order status — completed states get a check, cancelled/OOS get an X, rest get a plain package. */
const statusIcon: Record<OrderStatus, typeof Package> = {
  pending: Package,
  prepared: Package,
  collected: PackageCheck,
  administered: PackageCheck,
  out_of_stock: PackageX,
  returned: PackageX,
  cancelled: PackageX,
}

/** Coloured outline badge showing order fulfillment status (Pending, Ready, Collected, etc.). */
export function OrderStatusBadge({ status, size = 'default', title }: OrderStatusBadgeProps) {
  const Icon = statusIcon[status]
  return (
    <Badge size={size} variant={ORDER_STATUS_VARIANT[status]} title={title}>
      <Icon />
      {statusLabel[status]}
      {title?.trim() && <MessageSquareText className="size-2.5 opacity-70" aria-hidden="true" />}
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// PaymentBadge — outline badge with icon (Paid / Unpaid / nothing for free)
// ---------------------------------------------------------------------------

interface PaymentBadgeProps {
  order: { paymentStatus: PaymentStatus; totalPrice: number }
  size?: React.ComponentPropsWithoutRef<typeof Badge>['size']
}

/** Renders a Paid/Unpaid outline badge with a Banknote icon, or a grey "n/a" for free items. */
export function PaymentBadge({ order, size = 'default' }: PaymentBadgeProps) {
  const payment = getPaymentDisplay(order)
  if (!payment) return null
  return (
    <Badge size={size} variant={payment.badgeVariant}>
      <Banknote />
      {payment.label}
    </Badge>
  )
}
